import {OrderDirectionCalendar, OrderForm, Timetable, TimetableDate, TimetableGroup} from '~/types/types';
import {TimeTableRequest} from '~/types/api';
import {formatDate} from '~/common/date';
import {DispatchTimeV3, RawTimeTableV3} from '~/types/typesV3';
import { useDayjs } from '#dayjs'
const dayjs = useDayjs()

export type ConvertTimeTableOptions = {
  dispatchCalendar: OrderDirectionCalendar
  destinationCalendar: OrderDirectionCalendar
  softDestinationDate: boolean
  orderDates: { dispatch?: OrderDirectionCalendar, destination?: OrderDirectionCalendar }
}

// Шаблон даты
const dateObject = {
  isDefault: false,
  isAction: false,
  isDisabled: false,
  isDiscount: false,
  isExpress: false,
  isFaster: false,
  hasPaidWarehousing: false,
  date: '',
  optimalTime: {
    from: '',
    to: ''
  },
  defaultTime: '',
  note: '',
  stepMinute: 0,
  periods: []
}

export function getSplitIndex(timetable: Timetable|null, splitTime: number) {
  if (!timetable?.destination?.length || timetable?.destination?.length <= 1) {
    return 0
  }
  const findIndex = timetable.destination.findIndex(tt => {
    return splitTime <= tt.groupTimeMin
  })
  if (findIndex === - 1 && splitTime >= 1410) {
    // Максимальное значение времени
    return timetable.destination.length - 1
  }
  return findIndex
}

export function getTimetablePayload(order: OrderForm) {
  const { dispatch, destination } = order.directions

  const result: TimeTableRequest = {
    dispatch: {
      locationGuid: dispatch.locationGuid,
      date: formatDate(dispatch.calendar.date, 'YYYY-MM-DD')
    },
    destination: {
      locationGuid: destination.locationGuid,
      networkingGuid: destination.networkAddressGuid || undefined
    },
    orderGuid: order?.meta?.guid
  }

  if (dispatch.direction === 'terminal') {
    result.dispatch.terminalGuid = dispatch.terminalGuid || undefined
  }
  if (destination.direction === 'terminal') {
    result.destination.terminalGuid = destination.terminalGuid || undefined
  }
  if (destination.direction === 'pickupPoint') {
    result.destination.pickupPointGuid = destination.pickupPointGuid || undefined
  }

  return result
}

export function getDatesV3(
    timetable: DispatchTimeV3[],
    directionTo = false,
    destinationCalendar: OrderDirectionCalendar|undefined = undefined,
    hardDate = true
): TimetableDate[] {
  const days = timetable?.map(dispatchDate => dispatchDate.date) || []
  const calendarDates: any[] = []
  const dcDate = formatDate(destinationCalendar?.date, 'YYYY-MM-DD')
  const dcFrom = formatDate(`${dcDate} ${destinationCalendar?.from || '00:00'}`, 'YYYY-MM-DD HH:mm:ss')
  const dcTo = formatDate(`${dcDate} ${destinationCalendar?.to || '00:00'}`, 'YYYY-MM-DD HH:mm:ss')
  const dcTerminalTime = `${destinationCalendar?.date} ${destinationCalendar?.terminalTime || '00:00'}`

  const firstDate = destinationCalendar
      ? dayjs.min([dayjs(days[0]), dayjs(destinationCalendar.date)])
      : dayjs(days[0])
  const lastDate = destinationCalendar
      ? dayjs.max([dayjs(days[days.length - 1]), dayjs(destinationCalendar.date)])
      : dayjs(days[days.length - 1])

  const duration = dayjs.duration(lastDate.diff(firstDate)).asDays()

  // Отступы от активных дат
  for (let i = -3; i < duration + 3; i++) {
    const date = dayjs(firstDate).add(i, 'day').format('YYYY-MM-DD')
    const timetableDay = timetable?.find(timetableItem => timetableItem.date === date)

    if (timetableDay) {
      calendarDates.push({
        ...timetableDay,
        date,
        isDisabled: false,
        isDefault: directionTo ? false : timetableDay.isDefault,
        defaultTime: timetableDay.optimalTime?.from,
        periods: timetableDay.period || []
      })
    } else if (hardDate && date === destinationCalendar?.date) {
      calendarDates.push({
        isDefault: false,
        isAction: false,
        isExpress: false,
        isFaster: false,
        isDisabled: false,
        isDiscount: false,
        hasPaidWarehousing: false,
        date: destinationCalendar?.date,
        optimalTime: {
          from: dcFrom,
          to: dcTo
        },
        defaultTime: dcTerminalTime,
        note: '',
        stepMinute: 0,
        periods: [
          {
            type: 'day',
            from: dcFrom,
            to: dcTo,
            optimalTimeEnd: '',
            defaultFixTimeEnd: '',
            intervalHour: 0,
            note: '',
            stepMinute: 0
          }
        ]
      })
    } else {
      // Отступы
      calendarDates.push({
        date,
        isDisabled: true
      })
    }
  }

  if (directionTo) {
    const editDate = calendarDates.find(d => d.date === destinationCalendar?.date)
    if (editDate && !editDate.isDisabled) {
      editDate.isDefault = true

    } else {
      // Выбираем дефолтную дату, если она не заблокирована, или первую незаблокированую
      const notDisabled = calendarDates.find(d => {
        const timetableDate = timetable.find(item => item.date === d.date)
        return timetableDate?.isDefault && !d.isDisabled
      }) || calendarDates.find(d => !d.isDisabled)
      if (notDisabled) {
        notDisabled.isDefault = true
      }
    }
  }
  return calendarDates
}

export function getDispatchDatesV3(
    calendar: OrderDirectionCalendar,
    timetable: RawTimeTableV3['dispatch']
): TimetableDate[] {
  const dates: TimetableDate[] = []
  const date = formatDate(calendar.date, 'YYYY-MM-DD')
  const days = timetable?.map(dispatchDate => dispatchDate.date) || []
  const { optimalTime, period, isAction, isDiscount, isExpress, isFaster, hasPaidWarehousing, note }
      = timetable?.find(dispatchDate => dispatchDate.date === date) || {} as DispatchTimeV3
  const from = calendar.from ? formatDate(`${date} ${calendar.from}`, 'YYYY-MM-DD HH:mm:ss') : ''
  const to = calendar.to ? formatDate(`${date} ${calendar.to}`, 'YYYY-MM-DD HH:mm:ss') : ''

  let terminalTime = ''
  if (calendar.terminalTime) {
    terminalTime = calendar.terminalTime
  }

  for (let i = 3; i >= 1; i--) {
    const date = { ...dateObject, date: dayjs(calendar.date).subtract(i, 'day').format('YYYY-MM-DD') }
    const day = dayjs(calendar.date).subtract(i, 'day').format('YYYY-MM-DD')
    dates.push({
      ...date,
      isDisabled: !days.find(d => d === day),
    })
  }

  dates.push({
    isDefault: true,
    isAction,
    isExpress,
    isFaster,
    isDiscount,
    hasPaidWarehousing,
    isDisabled: false,
    date: calendar.date,
    optimalTime: {
      from,
      to
    },
    defaultTime: terminalTime ? `${calendar.date} ${terminalTime}` : optimalTime?.from || '',
    note,
    stepMinute: 0,
    periods: period as any
  })

  for (let i = 1; i <= 17; i++) {
    const date = { ...dateObject, date: dayjs(calendar.date).add(i, 'day').format('YYYY-MM-DD') }
    const day = dayjs(calendar.date).add(i, 'day').format('YYYY-MM-DD')
    dates.push({
      ...date,
      isDisabled: !days.find(d => d === day)
    })
  }

  return dates
}

export function getEmptyDestinationDates(calendar: OrderDirectionCalendar): TimetableGroup[] {
  const dates: TimetableDate[] = []
  for (let i = -3; i <= 10; i++) {
    const date = { ...dateObject, date: dayjs(calendar.date).add(i, 'day').format('YYYY-MM-DD') }
    const day = dayjs(calendar.date).add(i, 'day').format('YYYY-MM-DD')
    dates.push({
      ...date,
      isDefault: calendar.date === day
    })
  }

  return [{
    groupTime: '18:00',
    groupTimeMin: dayjs.duration({hours: 18}).asMinutes(),
    dates
  }]
}

/**
 * @param timetable
 * @param options - dispatchCalendar -> Выбранная дата отправления
 * destinationCalendar -> Выбранная дата прибытия
 * softDestinationDate -> Выбранная дата прибытия останется выбранной, только если она доступна
 * orderDates -> Даты отправки/прибытия сохранённого заказа
 */
export function convertTimeTableV3(timetable: RawTimeTableV3, options: Partial<ConvertTimeTableOptions>|undefined = undefined): Timetable {
  const localOptions = { ...options }
  let destination: TimetableGroup[] = []
  let dispatch: TimetableDate[]

  if (!localOptions.dispatchCalendar?.date) {
    localOptions.dispatchCalendar = undefined
  }
  if (!localOptions.destinationCalendar?.date) {
    localOptions.destinationCalendar = undefined
  }
  if (localOptions?.orderDates?.dispatch && !timetable.dispatch?.find(item => item.date === localOptions.orderDates?.dispatch?.date)) {
    // Добавляем дату из заказа для выбора
    const orderDispatchDate = getSimpleDateFromCalendar(localOptions.orderDates.dispatch)
    if (timetable.dispatch) {
      timetable.dispatch.unshift(orderDispatchDate)
    } else {
      timetable.dispatch = [orderDispatchDate]
    }
  }

  if (localOptions?.dispatchCalendar) {
    dispatch = getDispatchDatesV3(
        localOptions.dispatchCalendar,
        timetable.dispatch
    )
  } else {
    dispatch = getDatesV3(timetable.dispatch)
  }

  if (!timetable.destination?.length && localOptions.destinationCalendar) {
    destination = getEmptyDestinationDates(localOptions.destinationCalendar)
  } else {
    timetable.destination?.forEach((group) => {
      if (group.dates) {
        if (localOptions?.orderDates?.destination && !group.dates.find(item => item.date === localOptions.orderDates?.destination?.date)) {
          const orderDestinationDate = getSimpleDateFromCalendar(localOptions.orderDates.destination)
          group.dates.unshift(orderDestinationDate)
        }

        // if (destinationTimeTable) {
        const dayJsGroupSplit = dayjs(group.split)
        destination.push({
          groupTime: dayJsGroupSplit.format('HH:mm'),
          groupTimeMin: dayjs.duration({hours: dayJsGroupSplit.hour(), minutes: dayJsGroupSplit.minute()}).asMinutes(),
          dates: getDatesV3(group.dates, true, localOptions?.destinationCalendar, !localOptions?.softDestinationDate)
        })
        // }
      }
    })
  }

  return {
    dispatch,
    destination
  }
}

function getSimpleDateFromCalendar(calendar: OrderDirectionCalendar): DispatchTimeV3 {
  const dcFrom = dateTimeFormat(calendar?.date, calendar.from)
  const dcTo = dateTimeFormat(calendar?.date, calendar.to)
  const dcTerminalTime = dateTimeFormat(calendar?.date, calendar.terminalTime)
  return {
    date: calendar.date,
    optimalTime: {
      from: calendar.from
          ? dcFrom
          : dcTerminalTime,
      to: dcTo
    },
    isDefault: false,
    isAction: false,
    isExpress: false,
    isFaster: false,
    isDiscount: false,
    hasPaidWarehousing: false,
    period: [
      {
        type: 'day',
        from: dcFrom,
        to: dcTo,
        optimalTimeEnd: '',
        intervalHour: 0,
        note: '',
        stepMinute: 0
      }
    ],
  }
}

function dateTimeFormat(date: string|undefined, time: string|undefined) {
  if (!date) {
    return ''
  }
  return dayjs(`${date} ${time || '00:00'}`).format('YYYY-MM-DDTHH:mm:ss')
}
